import { ReactElement, useMemo } from 'react';
import { Skeleton } from 'ui/skeleton';
import { ColumnData, TableHeader } from 'ui/tableHeader';
import { TableRows, TableRowsColumn } from 'ui/tableRows';
import { TableCell } from 'ui/tableCell';
import { CampaignsSendMessagesLogsTableColumnData } from '../../types';

interface SkeletonTableRowsProps {
  numRows: number;
  cellHeight?: number;
  cellWidth?: number;
  tableColumnsConfig: ColumnData[];
}
export interface SkeletonTableColumnData extends TableRowsColumn, ColumnData {
  RowCell: () => ReactElement;
}

export const getSkeletonColumnConfig = (
  tableColumnsConfig: ColumnData[],
  cellHeight: number = 30,
  cellWidth: number = 200,
): SkeletonTableColumnData[] =>
  tableColumnsConfig.map((col) => ({
    ...col,
    RowCell: () => (
      <TableCell data-test="skeleton-cell">
        <Skeleton height={cellHeight} width={cellWidth} />
      </TableCell>
    ),
  }));

export const getSkeletonRowsConfig = (
  tableColumnsConfig: CampaignsSendMessagesLogsTableColumnData[],
  numRows: number,
) => {
  const columnIds = tableColumnsConfig.map((col) => col.value);

  return Array.from({ length: numRows }).map((_, ir) =>
    Object.fromEntries(
      [...columnIds, 'id'].map((key, i) =>
        key === 'id' ? [key, `skeleton-row-${i}-${ir}`] : [key, undefined],
      ),
    ),
  );
};

export const SkeletonTableContent = ({
  numRows,
  cellHeight,
  cellWidth,
  tableColumnsConfig,
}: SkeletonTableRowsProps) => {
  const tableColConfig: SkeletonTableColumnData[] = useMemo(
    () => getSkeletonColumnConfig(tableColumnsConfig, cellHeight, cellWidth),
    [tableColumnsConfig, cellHeight, cellWidth],
  );

  const rowsConfig = useMemo(
    () => getSkeletonRowsConfig(tableColConfig, numRows),
    [numRows, tableColConfig],
  );

  return (
    <>
      <TableHeader columns={tableColConfig} data-test="skeleton-table-header" />
      <TableRows rows={rowsConfig} columns={tableColConfig} data-test="skeleton-table-row" />
    </>
  );
};
